import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, get, useForm } from 'react-hook-form';
import z from 'zod';
import { IChatbotFlowModel } from '../../../data/models/chatbot';
import { IFlowModel } from '../../../data/models/flow';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { FormDropdownComponent } from '../_components/form-dropdowns';

const DropdownOptionsSchema = z.object({
  chatbotDropdowns: z
    .array(
      z.object({
        name: z.string().min(1, 'Dropdown option name is required'),
        varInputNodeId: z.string().min(1, 'Data input node is required'),
        options: z
          .array(
            z.object({
              value: z.string().min(1, 'Dropdown option value is required'),
              label: z.string().min(1, 'Dropdown option label is required'),
            }),
          )
          .nonempty('Must have at least one option'),
      }),
    )
    .default([]),
});

export type DropdownOptions = z.infer<typeof DropdownOptionsSchema>;

export function DropdownOptions({
  chatData,
  onSetFlow,
}: {
  chatData: IChatbotFlowModel;
  onSetFlow: (data: any) => void;
}) {
  const [dropdownOptionsCount, setDropdownOptionsCount] = useState(0);
  const [flow, setFlow] = useState<IFlowModel>({} as IFlowModel);
  const setNotification = useNotificationStore(state => state.setNotification);
  const formsMethods = useForm<DropdownOptions>({
    defaultValues: {
      chatbotDropdowns: [],
    },
    resolver: zodResolver(DropdownOptionsSchema),
  });

  useEffect(() => {
    if (chatData) {
      setDropdownOptionsCount(chatData.chatbotDropdowns?.length ?? 0);

      formsMethods.reset(
        {
          chatbotDropdowns: chatData.chatbotDropdowns ?? [],
        },
        { keepDefaultValues: true },
      );
    }
  }, [chatData, formsMethods]);

  async function handleSubmitForms(data: DropdownOptions) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        chatbotDropdowns: data.chatbotDropdowns,
      });

      onSetFlow(response.data);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      console.error(error);
      setNotification({
        type: 'error',
        message: 'Error saving dropdown options form',
      });
    }
  }

  useEffect(() => {
    async function getFlow() {
      const flow = await FlowService.getFlow(chatData.id);
      setFlow(flow.data);
    }

    getFlow();
  }, [chatData.id]);

  return (
    <FormProvider {...formsMethods}>
      <form
        id="dropdown-options"
        onSubmit={formsMethods.handleSubmit(handleSubmitForms)}
      >
        <Box
          width="100%"
          display="flex"
          gap="8px"
          alignItems="start"
          flexDirection="column"
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#101828',
            }}
          >
            Dropdown options
          </Typography>

          <FormControl>
            <FormLabel id="dropdown-options-label">
              Select the number of dropdown options
            </FormLabel>
            <RadioGroup
              aria-labelledby="dropdown-options-label"
              defaultValue="0"
              name="dropdown-count"
              value={dropdownOptionsCount.toString()}
              onChange={e => {
                setDropdownOptionsCount(Number(e.target.value));
              }}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
              }}
            >
              <FormControlLabel value="0" control={<Radio />} label="None" />
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
            </RadioGroup>
          </FormControl>

          {dropdownOptionsCount > 0 &&
            Array.from({ length: dropdownOptionsCount }).map((_, index) => (
              <>
                <FormDropdownComponent
                  key={index}
                  name="chatbotDropdowns"
                  index={index}
                  flow={flow}
                />

                {get(
                  formsMethods.formState.errors,
                  `chatbotDropdowns[${index}].options`,
                ) && (
                  <Typography color="red" fontSize="12px">
                    {
                      get(
                        formsMethods.formState.errors,
                        `chatbotDropdowns[${index}].options`,
                      ).message
                    }
                  </Typography>
                )}
              </>
            ))}
        </Box>
      </form>

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          form="dropdown-options"
          type="submit"
          variant="contained"
          sx={{ width: '160px' }}
        >
          Save changes
        </Button>
      </Box>
    </FormProvider>
  );
}
